// Per-config "how was this card last left" preference: which view, and which
// period granularity (the picker's W/M/6M/Y/All).
//
// The summary-card analogue of `charts/useChartPrefs`, and deliberately shaped
// like it: one module-local cache, one shared in-flight fetch, and a subscriber
// set to re-render the cards after a write — no React context, so no caller has
// to wrap its tree. The whole map is a handful of short strings, and it arrives
// in a single request no matter how many cards the dashboard renders.
//
// Server-backed rather than localStorage (see the backend note in
// `user_prefs.py`): the same person opens this app from the laptop, from the
// box's three HTTPS clone origins, and from their phone, and a per-origin store
// would make each of those open differently.
//
// Both axes live in one entry and are written by a merging PUT, so a card can
// persist the view it was switched to without knowing (or clobbering) the
// granularity, and vice versa.
//
// Stored values are validated at the CALL SITE, not here: the card knows the
// view cycle and the granularity set its config actually supports
// (`viewsForConfig` / `windowsForConfig`), so a value that no longer applies —
// a view dropped from a kind, a granularity a cadence change made meaningless —
// falls back to the default instead of leaving the picker in a
// selected-but-hidden state.

import { useEffect, useState } from 'react'
import {
  getSummaryCardPrefs,
  setSummaryCardPref,
  type SummaryCardPref,
  type SummaryCardPrefs,
} from '../../api/client'

let cache: SummaryCardPrefs | null = null
let inflight: Promise<SummaryCardPrefs> | null = null
const subscribers = new Set<() => void>()

const EMPTY: SummaryCardPref = {}

async function loadOnce(): Promise<SummaryCardPrefs> {
  if (cache) return cache
  if (inflight) return inflight
  inflight = (async () => {
    try {
      cache = await getSummaryCardPrefs()
    } catch {
      // A failed load must not cost the user their cards — fall back to "no
      // prefs" (every card opens on its defaults) and don't retry in a loop.
      cache = { cards: {} }
    } finally {
      inflight = null
    }
    return cache as SummaryCardPrefs
  })()
  return inflight
}

function notify() {
  subscribers.forEach((fn) => fn())
}

/** Test-only: drop the module cache so each test starts from "nothing stored".
 *  The cache is deliberately module-level (one fetch for every card on the
 *  page), which means it also survives between tests in a file. */
export function __resetSummaryCardPrefsCache(): void {
  cache = null
  inflight = null
}

export interface SummaryCardPrefsApi {
  /** False until the map has been fetched; a card shouldn't adopt stored state
   *  before then (it would flash the defaults and then jump). */
  loaded: boolean
  /** What's stored for this config — `{}` when nothing is. Raw: the caller
   *  checks each value against what its config can currently offer. */
  get(configId: number): SummaryCardPref
  /** Merge these axes into what's remembered for this config. Optimistically
   *  updates the local cache so the change is instant, then persists. */
  set(configId: number, patch: SummaryCardPref): Promise<void>
}

export function useSummaryCardPrefs(): SummaryCardPrefsApi {
  const [, setTick] = useState(0)

  useEffect(() => {
    let mounted = true
    const sub = () => {
      if (mounted) setTick((n) => n + 1)
    }
    subscribers.add(sub)
    if (!cache) loadOnce().then(() => mounted && setTick((n) => n + 1))
    return () => {
      mounted = false
      subscribers.delete(sub)
    }
  }, [])

  return {
    loaded: cache !== null,
    get(configId) {
      return cache?.cards[String(configId)] ?? EMPTY
    },
    async set(configId, patch) {
      const key = String(configId)
      // Write through the cache first: the card has already changed, and a slow
      // (or failed) request shouldn't make a sibling card disagree about how
      // this config is being viewed. Merged, matching the endpoint.
      cache = {
        cards: {
          ...(cache?.cards ?? {}),
          [key]: { ...(cache?.cards?.[key] ?? {}), ...patch },
        },
      }
      notify()
      try {
        cache = await setSummaryCardPref(configId, patch)
        notify()
      } catch (e) {
        console.error('Failed to save summary card preference', e)
      }
    },
  }
}
